import type { BiocideFamilyApiResponse, ProduitType } from '@b2ag/module-product-sdk'
import { type Profile } from '../domain/product'

interface ActiveSubstance {
  name: string
  unit: string
  value: number
}

export interface ProductCategory {
  code: string
  label: string
}

export type ProductCategories = ProductCategory[][]

interface Toxicity {
  id: string
  name: string
}

interface UsageByCrop {
  crop_id: string
  crop_name: string
}

interface AssociatedProduct {
  _id: string
  categories: ProductCategories
  imageUrl: string
  name: string
  supplier: string
  kind: string
  sellable: boolean
}

type AssociatedVariant = {
  id: string
}

type Service = {
  groupId: string
  service_type: string
  associatedVariants: AssociatedVariant[]
}

export type AssociatedServices = {
  mandatory_services: Service[]
  optional_services: Service[]
}

export interface ProductVariant {
  associatedServices: AssociatedServices
  supplier?: string
  associatedSeedTreatmentManagement?: string
  ean?: string
  heightInM?: number
  id: string
  isSoldAsUnit: boolean
  length?: number
  lengthInM?: number
  measure_quantity: number
  measure_unit: string
  name: string
  packaging?: string
  precocity?: string
  productId: string
  productKind: string
  productUnit?: string
  sellable?: boolean
  area?: number
  height?: number
  totalLengthM?: number
  offline?: boolean
  type?: string
  productConditionning?: string
  /** Weight in kilograms */
  weight?: number
  width?: number
  widthUnit?: string
  restrictedToProfiles?: Array<Profile>
  sellable_standalone?: boolean
}

interface BaseProduct {
  _id: string
  slug: string
  alternation?: string
  associatedProducts?: AssociatedProduct[]
  available_slugs?: Record<string, string>
  isMentionBioControl?: boolean
  biocideFamily?: BiocideFamilyApiResponse[]
  biocide_use?: Array<{ code: string; label: string }>
  categories: ProductCategories
  characteristics?: string
  chlortoluron_tolerance?: string
  color?: string
  contributionMode?: string
  cropName?: string
  description?: string
  displayType?: string
  energyType?: string[]
  productType?: string
  exclusive?: boolean
  /** @deprecated use 'main_category[0].label' instead */
  family?: string
  frontHighlight?: string[]
  generation?: string
  imageUrl?: string
  medias: string[]
  isHighlighted?: boolean
  isCooperativeProduct?: boolean
  isRestrictedExplosivesPrecursor: boolean
  isSupplierHighlighted?: boolean
  isExcluTech?: boolean
  isOffSeason?: boolean
  kind: string
  main_category: ProductCategory[]
  marketingEndDatePm?: string
  compositionMix?: string[]
  mixed?: boolean
  name: string
  chemicals_elements?: {
    composition?: {
      name: string
      value: string
      unit: string
    }[]
    name: string
    value: string
    unit: string
  }[]
  isMentionOrganic?: boolean
  origin?: string
  productManualUrls?: string[]
  productSheetUrls?: string[]
  documentation?: Record<string, string[]>
  genetic_profil?: string
  obtenteur?: string
  recommendationForUse?: {
    crops?: string
    application_guidelines?: string
  }[]
  resistance?: string
  engraisPackagingForm?: string
  standard?: string
  stimulatingSubstance?: string
  supplier?: string
  supplierLogo?: string
  supplierUniversePageLink?: string
  supplierScore?: number
  targets?: {
    id: string
    name: string
  }[]
  type?: ProduitType
  typeEng?: string
  useEndDatePm?: string
  valuation?: string[]
  variants: ProductVariant[]
  meta_title?: string
  meta_description?: string
}

interface SingleProductAttributes {
  active_substances?: ActiveSubstance[]
  cropProtectionProductFormulationName?: string
  market_authorization?: string
  safetySheetUrls?: string[]
  toxicities?: Toxicity[]
  associatedUses?: UsageByCrop[]
}

export interface FlattenedAttribute<T> {
  product_name: string
  value: T
}

export interface FlattenedPackProductAttributes {
  active_substances: FlattenedAttribute<ActiveSubstance[]>[]
  safetySheetUrls: FlattenedAttribute<string[] | undefined>[]
  cropProtectionProductFormulationName: FlattenedAttribute<string>[]
  market_authorization: FlattenedAttribute<string>[]
  toxicities: FlattenedAttribute<Toxicity[]>[]
  associatedUses: {
    id: number
    product_name: string
    value: UsageByCrop[]
  }[]
}

export interface CooperativeVariantFields {
  associated?: boolean
  cooperativeVariantId?: number
  isSoldAsUnit: boolean
  published?: boolean
  stockManagementType?: string
  stockQuantity?: number
  taxes?: {
    rpd: number
    rpdUnit: string
    tva: number
  }
}

export type CooperativeVariant = ProductVariant & CooperativeVariantFields

interface CooperativeProductFields {
  cooperativeReview?: {
    tips?: string
    upsides?: string
  }
  variants: CooperativeVariant[]
  associatedProducts?: AssociatedProduct[]
}

type SingleProduct = BaseProduct & SingleProductAttributes

export type FlattenedPackProduct = BaseProduct & FlattenedPackProductAttributes
export type Product = SingleProduct | FlattenedPackProduct

export type CooperativeProduct = Omit<Product, 'variants'> & CooperativeProductFields

export type ProductRedirect = {
  redirectUrl: string
}

export class ProductRedirectError extends Error {
  constructor(public redirectUrl: string) {
    super(redirectUrl)
    Object.setPrototypeOf(this, ProductRedirectError.prototype)
  }
}

export enum TagKind {
  PROMO = 'promo',
  EXCLUSIF = 'exclusif',
  INNOVATION = 'innovation',
}
